"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { FaArrowLeft } from "react-icons/fa";
import { Song } from "@/app/types/types";
import AudioPlayer from "../../components/AudioPlayer";
import { motion } from "framer-motion";
import { useCart } from "../../context/CartContext";

async function getSongDetails(id: string): Promise<Song> {
  const res = await fetch(`/api/songs/${id}`, { next: { revalidate: 60 } });
  if (!res.ok) {
    throw new Error("Failed to fetch song details");
  }
  return res.json();
}

// New animated loading component
const AnimatedLoading = () => {
  return (
    <div className="h-screen flex flex-col items-center justify-center text-white">
      <motion.div
        className="w-16 h-16 border-t-4 border-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      <motion.p
        className="mt-4 text-xl sm:text-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
      >
        Loading...
      </motion.p>
    </div>
  );
};

export default function SongDetails({
  params,
}: {
  params: { songId: string };
}) {
  const [songDetails, setSongDetails] = useState<Song | null>(null);
  const { addToCart } = useCart();

  useEffect(() => {
    getSongDetails(params.songId).then(setSongDetails);
  }, [params.songId]);

  const getSongIdFromUrl = (url: string) => {
    const regex =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regex);
    return match && match[2].length === 11 ? match[2] : null;
  };

  if (!songDetails) return <AnimatedLoading />;
  console.log(songDetails);

  const handleAddToCart = () => {
    addToCart(songDetails);
  };

  // Rest of the component remains the same
  return (
    <motion.div
      className="font-goodfont py-8 sm:py-16 bg-transparent text-white min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            href="/music"
            className="text-gray-400 flex items-center hover:text-white transition-colors duration-300 mb-6 sm:mb-8"
          >
            <FaArrowLeft className="mr-2" />
            Back to Music
          </Link>
        </motion.div>

        <motion.h1
          className="text-3xl sm:text-4xl md:text-5xl mb-8 sm:mb-12 text-center uppercase tracking-widest"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {songDetails.title}
        </motion.h1>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 mb-8 sm:mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <motion.div
            className="border border-white p-4 bg-black"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <Image
              src={songDetails.coverArt}
              alt={songDetails.title}
              width={500}
              height={500}
              className="w-full h-auto"
            />
          </motion.div>
          <div className="border border-white p-4 sm:p-6 bg-black">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div>
                <p className="text-base sm:text-lg text-gray-400 mb-1">
                  Instrumentation:
                </p>
                <p className="text-lg sm:text-xl">
                  {songDetails.instrumentation}
                </p>
              </div>
              <div>
                <p className="text-base sm:text-lg text-gray-400 mb-1">
                  Medium:
                </p>
                <p className="text-lg sm:text-xl">{songDetails.medium}</p>
              </div>
              <div>
                <p className="text-base sm:text-lg text-gray-400 mb-1">
                  Difficulty:
                </p>
                <p className="text-lg sm:text-xl">{songDetails.difficulty}</p>
              </div>
              <div>
                <p className="text-base sm:text-lg text-gray-400 mb-1">
                  Duration:
                </p>
                <p className="text-lg sm:text-xl">
                  {songDetails.duration} minutes
                </p>
              </div>
              <div>
                <p className="text-base sm:text-lg text-gray-400 mb-1">Year:</p>
                <p className="text-lg sm:text-xl">{songDetails.year}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <motion.a
                href={songDetails.previewPdf}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center text-base sm:text-lg bg-black border border-white px-4 sm:px-6 py-2 sm:py-3 hover:bg-white hover:text-black transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Preview Score
              </motion.a>
              <motion.a
                href={
                  songDetails.stripePaymentLink &&
                  songDetails.stripePaymentLink.startsWith("http")
                    ? songDetails.stripePaymentLink
                    : `https://${songDetails.stripePaymentLink}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center text-base sm:text-lg bg-black border border-white px-4 sm:px-6 py-2 sm:py-3 hover:bg-white hover:text-black transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Purchase Score
              </motion.a>
              <motion.button
                onClick={handleAddToCart}
                className="flex-1 text-center text-base sm:text-lg bg-black border border-white px-4 sm:px-6 py-2 sm:py-3 hover:bg-white hover:text-black transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Add to Cart
              </motion.button>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="mb-8 sm:mb-12 border border-white p-4 sm:p-6 bg-black"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h2 className="text-xl sm:text-2xl mb-3 sm:mb-4 uppercase tracking-wider">
            Description:
          </h2>
          {songDetails.description.split("\n").map((paragraph, index) => (
            <p
              key={index}
              className="text-base sm:text-lg text-gray-300 mb-3 sm:mb-4"
            >
              {paragraph}
            </p>
          ))}
        </motion.div>

        {songDetails.audioUrl && (
          <motion.div
            className="mb-8 sm:mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <h2 className="text-xl sm:text-2xl mb-3 sm:mb-4 uppercase tracking-wider">
              Audio Preview:
            </h2>
            <AudioPlayer
              audioUrl={songDetails.audioUrl}
              songTitle={songDetails.title}
            />
          </motion.div>
        )}

        {songDetails.youtubeLink && (
          <motion.div
            className="mb-8 sm:mb-12 border border-white p-4 sm:p-6 bg-black"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1 }}
          >
            <h2 className="text-xl sm:text-2xl mb-3 sm:mb-4 uppercase tracking-wider">
              Video:
            </h2>
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src={`https://www.youtube.com/embed/${getSongIdFromUrl(
                  songDetails.youtubeLink
                )}`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full aspect-video"
              />
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
